import React, { useCallback } from "react";
import {
  CAMERA_GRID_PREVIEW_LIMIT,
  formatAsOfLabel,
  getDistributionTimezone,
  type PeopleDistributionData,
  type PeopleDistributionFilters,
} from "../../services/peopleDistributionTypes";
import { getCameraGridRest } from "../../services/peopleDistributionService";
import { CameraGridCurrent } from "./CameraGridCurrent";
import { DistributionKpiSection } from "./DistributionKpiSection";
import { DwellTrendChart } from "./DwellTrendChart";
import { HourlyPeakHeatmap } from "./HourlyPeakHeatmap";
import { OccupancyTrendChart } from "./OccupancyTrendChart";
// import { PeakPerClassChart } from "./PeakPerClassChart";

const cardClass =
  "rounded-xl border border-slate-200 bg-white dark:border-slate-700/60 dark:bg-[#161b2b]";

type PeopleDistributionDashboardProps = {
  data: PeopleDistributionData;
  selectedDate?: string;
  filters: PeopleDistributionFilters;
  availableClasses: string[];
};

export const PeopleDistributionDashboard: React.FC<PeopleDistributionDashboardProps> = ({
  data,
  selectedDate,
  filters,
  availableClasses,
}) => {
  const totalCount = data.cameraGridTotal ?? data.cameraGrid.length;

  const handleLoadMoreCameras = useCallback(async () => {
    const remaining = Math.max(0, totalCount - CAMERA_GRID_PREVIEW_LIMIT);
    if (remaining <= 0) return [];
    return getCameraGridRest(filters, availableClasses, CAMERA_GRID_PREVIEW_LIMIT, remaining);
  }, [availableClasses, filters, totalCount]);

  return (
    <div className="space-y-4">
      <DistributionKpiSection kpis={data.kpis} />

      <div className={`${cardClass} p-4`}>
        <OccupancyTrendChart
          data={data.occupancyTrend}
          activeClasses={data.activeClasses}
          peakOccupancy={data.kpis?.peakOccupancy}
          peakBucketTime={data.kpis?.peakBucketTime}
          peakOccupancyByType={data.kpis?.peakOccupancyByType}
          selectedDate={selectedDate}
        />
      </div>

      <div className={`${cardClass} p-4`}>
        <DwellTrendChart data={data.dwellTrend} selectedDate={selectedDate} />
      </div>

      <div className={`${cardClass} p-4`}>
        <HourlyPeakHeatmap rows={data.hourlyHeatmap} hours={data.heatmapHours} />
      </div>

      {/* <div className={`${cardClass} p-4`}>
        <PeakPerClassChart
          data={data.hourlyClassPeaks}
          selectedDate={selectedDate}
          throughHour={data.throughHour}
        />
      </div> */}

      <div className={`${cardClass} p-4`}>
        <CameraGridCurrent
          cameras={data.cameraGrid}
          totalCount={totalCount}
          onLoadMore={handleLoadMoreCameras}
        />
      </div>

      {data.asOf ? (
        <p className="text-center text-[11px] text-slate-500 dark:text-slate-400">
          Data as of {formatAsOfLabel(data.asOf, getDistributionTimezone())}
        </p>
      ) : null}
    </div>
  );
};

export default PeopleDistributionDashboard;
